"use client"

import { useCallback, useEffect, useMemo, type ReactNode } from "react"

import { useParams, usePathname, useRouter, useSearchParams } from "next/navigation"

import { ListingError, ListingShell, type FacetOption } from "@/components/marketplace"
import { ListingEmpty } from "@/components/marketplace/listing/listing-empty"
import type { Product } from "@/components/marketplace/types"
import type { Crumb } from "@/components/ui"
import { categoryExitRoute, categorySwitchUpdates, searchExitRoute } from "@/features/browse/listing/category-change"
import { attributeFacets, categoryFacetLabels, categoryTreeWithResults, offeredCategories } from "@/features/browse/listing/category-facet"
import { describeQueryUpdate } from "@/features/browse/listing/describe-query-update"
import { withinPriceBand } from "@/features/browse/listing/price-band"
import { mapSearchProduct } from "@/features/feed/map-product"
import { localizePath } from "@/features/feed/resolve-link"
import type { Result as SearchProduct } from "@/generated/api/types.gen"
import { useInfiniteSearchData } from "@/lib/hooks/search/use-search-data"
import { useSearchQuery } from "@/lib/hooks/search/use-search-query"
import { useBatchedViewTracking } from "@/lib/hooks/use-batched-view-tracking"
import { buildListingPagination, listingPageFromQueryString, listingParamsOnly, unpagedTitle } from "@/lib/listing-pagination"
import { DEFAULT_REGION } from "@/lib/region"
import { buildRoute, pathWithQuery, ROUTES } from "@/lib/routes"
import { listingBaseQuery } from "@/lib/services/search/search"
import type { SearchPageData, SearchQuery } from "@/lib/types/search"
import { useNavigationPath } from "@/platform/app-mode/NavigationProvider"
import { createListIdentifiers } from "@/platform/catalog/products.client"
import { buildGA4ItemFromSearchProduct } from "@/platform/observability/analytics/v2/mappers"
import { trackSelectContent, trackSelectItem } from "@/platform/observability/analytics/v2/search"
import { logger } from "@/platform/observability/logger"

interface CanonicalParam {
  key: string
  from: string
  to: string
}

interface ListingPageClientProps {
  countryCode?: string
  baseQuery?: Partial<SearchQuery>
  initialData?: SearchPageData
  breadcrumbs?: Crumb[]
  title: ReactNode
  subline?: ReactNode
  countKeyword?: string
  banner?: ReactNode
  categoryOptions?: FacetOption[]
  categoryLabels?: Record<string, string>
  categoriesHref?: string
  canonicalParams?: CanonicalParam[]
  paginate?: boolean
}

export const ListingPageClient = ({
  countryCode,
  baseQuery,
  initialData,
  breadcrumbs,
  title,
  subline,
  countKeyword,
  banner,
  categoryOptions,
  categoryLabels,
  categoriesHref,
  canonicalParams,
  paginate = false,
}: ListingPageClientProps) => {
  const params = useParams<{ countryCode?: string }>()
  const pathname = usePathname()
  const router = useRouter()
  const navigationPath = useNavigationPath()
  const searchParams = useSearchParams()
  const region = countryCode ?? params.countryCode ?? DEFAULT_REGION
  const memoBaseQuery = useMemo(() => listingBaseQuery(baseQuery), [baseQuery])
  const { query, params: renderedQuery, updateQuery } = useSearchQuery({ baseQuery: memoBaseQuery })

  // The server resolves facet handles (feed links emit handles) to the ids the search
  // backend filters on, but every client fetch is rebuilt from the raw URL — so a
  // handle-carrying URL makes load-more and filter refetches return empty. Rewrite the
  // URL once to the resolved ids; each entry only fires while the param still equals
  // the exact raw value the server resolved, so user edits are never clobbered.
  useEffect(() => {
    if (!canonicalParams?.length) return
    const params = new URLSearchParams(searchParams.toString())
    let changed = false
    for (const { key, from, to } of canonicalParams) {
      if (params.get(key) === from) {
        params.set(key, to)
        changed = true
      }
    }
    if (changed) router.replace(pathWithQuery(pathname, params.toString()), { scroll: false })
  }, [canonicalParams, searchParams, pathname, router])

  const startPage = paginate ? listingPageFromQueryString(renderedQuery) : 1
  const { data, error, refetch, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isPlaceholderData } = useInfiniteSearchData(query, initialData, {
    includeFacets: true,
    renderedQuery,
    keepPreviousResults: true,
    startPage,
  })

  const inPriceBand = useMemo(() => withinPriceBand(query), [query])
  const pages = useMemo(() => (data?.pages ?? []).map((page) => ({ ...page, products: page.products.filter(inPriceBand) })), [data, inPriceBand])
  const productPages = useMemo(() => pages.map((page) => ({ serveId: page.serveId, products: page.products.map((product) => mapSearchProduct(product, region)) })), [pages, region])
  const products = useMemo(() => productPages.flatMap((page) => page.products), [productPages])

  const totalCount = data?.pages[0]?.totalCount
  const resultFacets = data?.pages[0]?.facets
  const facets = useMemo(() => attributeFacets(resultFacets), [resultFacets])
  const categoryTree = useMemo(
    () => (categoryOptions ? categoryTreeWithResults(categoryOptions, resultFacets, query.categories) : undefined),
    [categoryOptions, resultFacets, query.categories]
  )
  const offeredCategoryOptions = useMemo(() => (categoryTree ? offeredCategories(categoryTree, query.categories) : undefined), [categoryTree, query.categories])
  const labels = useMemo(() => ({ ...categoryFacetLabels(resultFacets), ...categoryLabels }), [resultFacets, categoryLabels])
  const loading = isFetchingNextPage || (products.length === 0 && isFetching)
  const firstPageNumber = data?.pages[0]?.pageNumber ?? 1
  const pagination = useMemo(
    () =>
      paginate && totalCount !== undefined && !isPlaceholderData
        ? buildListingPagination({
            pathname,
            queryString: listingParamsOnly(renderedQuery),
            page: startPage,
            totalCount,
            pageSize: query.limit,
          })
        : undefined,
    [paginate, totalCount, isPlaceholderData, pathname, renderedQuery, startPage, query.limit]
  )

  useEffect(() => {
    if (error) logger.error("listing.search.failed", { error, query })
  }, [error, query])

  const { listId, listName } = useMemo(() => {
    if (!pathname) return { listId: "none", listName: "none" }
    const title = typeof document !== "undefined" ? unpagedTitle(document.title) : ""
    return createListIdentifiers(pathname, searchParams, title)
  }, [pathname, searchParams])

  const { trackProducts } = useBatchedViewTracking(listId, listName)

  useEffect(() => {
    for (const page of pages) {
      trackProducts(page.products, (page.pageNumber - 1) * query.limit)
    }
  }, [pages, trackProducts, query.limit])

  const applyUpdate = useCallback(
    (updates: Partial<SearchQuery>) => {
      for (const params of describeQueryUpdate(updates, query)) trackSelectContent(params)
      updateQuery(updates)
    },
    [query, updateQuery]
  )

  const handleUpdateQuery = useCallback(
    (updates: Partial<SearchQuery>) => {
      for (const params of describeQueryUpdate(updates, query)) trackSelectContent(params)
      const exitRoute = categoryExitRoute(memoBaseQuery, updates, categoryTree) ?? searchExitRoute(query, updates, categoryTree)
      if (exitRoute) return router.push(navigationPath(localizePath(exitRoute, region)))
      updateQuery(categorySwitchUpdates(query, updates, categoryTree))
    },
    [memoBaseQuery, query, categoryTree, updateQuery, router, region, navigationPath]
  )

  const searchProductById = useMemo(() => {
    const byId = new Map<string, SearchProduct>()
    for (const page of pages) for (const product of page.products) byId.set(product.productGroupUuid, product)
    return byId
  }, [pages])

  const handleProductClick = (product: Product, index: number) => {
    const searchProduct = searchProductById.get(product.id)
    if (!searchProduct) return
    trackSelectItem({
      item_list_id: listId,
      item_list_name: listName,
      items: [buildGA4ItemFromSearchProduct(searchProduct, { index: (firstPageNumber - 1) * query.limit + index, item_list_id: listId, item_list_name: listName })],
    })
  }

  const clearAll = () => applyUpdate({ categories: [], collections: [], attributes: {}, priceMin: undefined, priceMax: undefined, inStockOnly: true })

  const empty = (
    <ListingEmpty
      query={query}
      region={region}
      categoryLabels={labels}
      categoryOptions={offeredCategoryOptions}
      facets={facets}
      onUpdateQuery={handleUpdateQuery}
      onClearAll={clearAll}
      categoriesHref={categoriesHref ?? localizePath(buildRoute.category(), region)}
      homeHref={localizePath(`/${ROUTES.HOME}`, region)}
    />
  )

  return (
    <ListingShell
      breadcrumbs={breadcrumbs}
      title={title}
      subline={subline}
      countKeyword={countKeyword}
      banner={banner}
      query={query}
      categoryOptions={offeredCategoryOptions}
      categoryLabels={labels}
      facets={facets}
      onUpdateQuery={handleUpdateQuery}
      products={products}
      renderedQuery={renderedQuery}
      totalCount={totalCount}
      hasMore={hasNextPage && !isPlaceholderData}
      loading={loading}
      refreshing={isPlaceholderData}
      onLoadMore={() => void fetchNextPage()}
      empty={empty}
      error={error ? <ListingError onRetry={() => void refetch()} /> : undefined}
      onProductClick={handleProductClick}
      pages={productPages}
      pagination={pagination}
    />
  )
}
